import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { ConfirmDialog, Field, Modal, MoneyInput, StatusBadge, useToast } from "@cx/ui";
import type { PixChargeView, TitleDetail } from "@cx/contracts";
import { formatBRL } from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { CHARGE_BADGE, EXPIRATION_OPTIONS, PAYMENT_STATUS_LABEL, reviewLabel } from "./labels";
import { QrCode } from "./QrCode";

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Cobrança Pix da conta a receber (T1.10): gerar (QR + copia e cola), acompanhar a situação (atualiza sozinho
 * enquanto há Pix aguardando), consultar no provedor, cancelar e, no provedor de testes, simular o pagamento.
 */
export function PixPanel({
  title,
  canUpdate,
  onPaid,
}: {
  title: TitleDetail;
  canUpdate: boolean;
  onPaid(): void;
}) {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [generating, setGenerating] = useState(false);
  const [canceling, setCanceling] = useState<PixChargeView | null>(null);
  const key = ["pix-charges", companyId, title.id];
  const charges = useQuery({
    queryKey: key,
    queryFn: () => api<PixChargeView[]>(`/pix-charges?titleId=${title.id}`),
    refetchInterval: (q) =>
      q.state.data?.some((c) => c.status === "active" || c.payments.some((p) => p.status === "received"))
        ? 3000
        : false,
  });
  // Pagamento processado desde a última leitura (baixa automática): atualiza a conta e o extrato.
  const processed = (charges.data ?? []).reduce(
    (n, c) => n + c.payments.filter((p) => p.status !== "received").length,
    0,
  );
  const seen = useRef<number | null>(null);
  useEffect(() => {
    if (!charges.data) return;
    if (seen.current !== null && processed > seen.current) onPaid();
    seen.current = processed;
  }, [charges.data, processed, onPaid]);
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const post = (path: string, body?: unknown, idem = true) =>
    api<unknown>(path, {
      method: "POST",
      headers: idem ? { "Idempotency-Key": crypto.randomUUID() } : undefined,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  const cancel = useMutation({
    mutationFn: (id: string) => post(`/pix-charges/${id}/cancel`),
    onSuccess: () => {
      toast("success", "Pix cancelado");
      refresh();
    },
    onError: (e: Error) => {
      toast("danger", errorMessage(e));
      refresh();
    },
  });
  const check = useMutation({
    mutationFn: (id: string) => post(`/pix-charges/${id}/refresh`, undefined, false),
    onSuccess: () => {
      toast("success", "Situação consultada no provedor");
      refresh();
      onPaid();
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const simulate = useMutation({
    mutationFn: (id: string) => post(`/pix-charges/${id}/simulate-payment`, {}),
    onSuccess: () => {
      toast("success", "Pagamento simulado enviado");
      refresh();
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const list = charges.data ?? [];
  const live = list.find((c) => c.status === "active" || c.status === "creating");
  const open = title.status === "open" || title.status === "partial";

  return (
    <div className="box card mb-3">
      <div className="d-flex flex-wrap align-items-center gap-2 px-3 pt-3 pb-2">
        <h2 className="h6 mb-0 me-auto">Cobrança Pix</h2>
        {canUpdate && open && !live && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setGenerating(true)}>
            Gerar Pix
          </button>
        )}
      </div>
      {charges.error && <p className="text-danger px-3">{errorMessage(charges.error)}</p>}
      {list.length === 0 && !charges.isLoading && (
        <p className="text-muted px-3 pb-3 mb-0">Nenhum Pix gerado para esta conta.</p>
      )}
      {list.map((c) => {
        const b = CHARGE_BADGE[c.status];
        return (
          <div key={c.id} className="border-top px-3 py-3" data-testid="pix-charge">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
              <StatusBadge tone={b.tone}>{b.label}</StatusBadge>
              <span>
                Valor <strong>{formatBRL(c.amountCents)}</strong>
                {c.interestCents + c.fineCents > 0 && (
                  <span className="text-muted">
                    {" "}
                    (inclui {formatBRL(c.interestCents + c.fineCents)} de juros e multa)
                  </span>
                )}
              </span>
              <span className="text-muted small">
                Gerado em {dateTime(c.createdAt)} · vence em {dateTime(c.expiresAt)}
              </span>
            </div>
            {c.status === "failed" && c.errorMessage && <p className="text-danger mb-2">{c.errorMessage}</p>}
            {c.status === "active" && c.emv && (
              <div className="d-flex flex-wrap gap-3 align-items-start mb-2">
                <QrCode value={c.emv} label="QR Code do Pix" />
                <div className="flex-grow-1" style={{ minWidth: 0, maxWidth: 520 }}>
                  <label className="form-label" htmlFor={`emv-${c.id}`}>
                    Pix copia e cola
                  </label>
                  <textarea
                    id={`emv-${c.id}`}
                    className="form-control font-monospace small"
                    rows={4}
                    readOnly
                    value={c.emv}
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() =>
                        navigator.clipboard
                          .writeText(c.emv!)
                          .then(() => toast("success", "Código copiado"))
                          .catch(() => toast("danger", "Não foi possível copiar; selecione o texto."))
                      }
                    >
                      Copiar código
                    </button>
                    {canUpdate && (
                      <>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={check.isPending}
                          onClick={() => check.mutate(c.id)}
                        >
                          Consultar no provedor
                        </button>
                        {c.provider === "sandbox" && (
                          <button
                            type="button"
                            className="btn btn-outline-success btn-sm"
                            disabled={simulate.isPending}
                            onClick={() => simulate.mutate(c.id)}
                          >
                            Simular pagamento
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          disabled={cancel.isPending}
                          onClick={() => setCanceling(c)}
                        >
                          Cancelar Pix
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            {c.payments.length > 0 && (
              <ul className="list-unstyled small mb-0" aria-label="Pagamentos Pix recebidos">
                {c.payments.map((p) => (
                  <li key={p.id}>
                    {dateTime(p.paidAt)} · {formatBRL(p.amountCents)} · {PAYMENT_STATUS_LABEL[p.status]}
                    {reviewLabel(p.reviewReason) && <> ({reviewLabel(p.reviewReason)})</>}
                    <span className="text-muted"> · {p.endToEndId}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      {generating && (
        <GeneratePixDialog
          title={title}
          onClose={() => setGenerating(false)}
          onCreated={() => {
            refresh();
          }}
        />
      )}
      <ConfirmDialog
        open={!!canceling}
        title="Cancelar Pix"
        message="Cancelar este Pix? O QR Code deixa de aceitar pagamento."
        confirmLabel="Cancelar Pix"
        onConfirm={() => {
          if (canceling) cancel.mutate(canceling.id);
          setCanceling(null);
        }}
        onCancel={() => setCanceling(null)}
      />
    </div>
  );
}

function GeneratePixDialog({
  title,
  onClose,
  onCreated,
}: {
  title: TitleDetail;
  onClose(): void;
  onCreated(): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [amount, setAmount] = useState<number | null>(title.openCents);
  const [expiration, setExpiration] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [key] = useState(() => crypto.randomUUID());
  const save = useMutation({
    mutationFn: () =>
      api<PixChargeView>("/pix-charges", {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ titleId: title.id, amountCents: amount, expirationSeconds: expiration }),
      }),
    onSuccess: () => {
      toast("success", "Pix gerado");
      onCreated();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
      onCreated();
    },
  });
  return (
    <Modal
      open={open}
      title="Gerar Pix"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Gerar Pix
          </button>
        </>
      }
    >
      <div
        className="row"
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target instanceof HTMLInputElement) e.preventDefault();
        }}
      >
        <Field
          label="Valor"
          required
          className="col-md-6"
          error={errors.amountCents}
          hint={`Saldo em aberto: ${formatBRL(title.openCents)}. Vencida: juros e multa do dia são somados.`}
        >
          <MoneyInput value={amount} onChange={setAmount} />
        </Field>
        <Field label="Validade" className="col-md-6" error={errors.expirationSeconds}>
          <select
            className="form-select"
            value={expiration ?? ""}
            onChange={(e) => setExpiration(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Padrão da configuração</option>
            {EXPIRATION_OPTIONS.map((o) => (
              <option key={o.seconds} value={o.seconds}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  );
}
